import type { TokenCredential } from "@azure/identity";
import { AIProjectClient, type HostedAgentDefinition, type PromptAgentDefinition, type ReasoningEffort } from "@azure/ai-projects";
import { authedJson, authedJsonResult } from "./http.js";
import { LocalCliError } from "./errors.js";

const METADATA_SOURCE = "m8t-stack-POC";

const FOUNDRY_SCOPE = "https://ai.azure.com/.default";
const API = "v1";

export interface CoderMetadata {
  source: "m8t-stack-POC";
  kind: "hosted";
  persona: string;
  personaVersion: string | null;
  [key: string]: string | null; // merge-preserved unmanaged keys (a2a, brain, …) — F6
}

/**
 * Create a hosted coding-agent version via the @azure/ai-projects SDK
 * (the F1-proven TS path). Returns the new version id (e.g. "1").
 *
 * NOTE: the SDK's HostedAgentDefinition uses snake_case fields
 * (`container_protocol_versions` / `environment_variables`), matching the
 * live wire shape F1 observed. Returns the version as a string.
 */
export async function createCoderVersion(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  image: string;
  cpu: string;
  memory: string;
  env: Record<string, string>;
  metadata: CoderMetadata;
  brain?: string;
}): Promise<string> {
  const project = new AIProjectClient(args.endpoint, args.credential);

  const definition: HostedAgentDefinition = {
    kind: "hosted",
    image: args.image,
    cpu: args.cpu,
    memory: args.memory,
    container_protocol_versions: [{ protocol: "responses", version: "1.0.0" }],
    environment_variables: args.env,
  };

  // F6: spread the caller's full metadata map first so unmanaged keys (a2a,
  // a2aCard, a2aBearerHash, a stale brain, …) reach the real API payload —
  // deploy.ts's redeploy merge is only meaningful if it survives this call.
  // Null-valued keys (personaVersion when absent) are dropped from the spread
  // since the payload is string-only; the branch below re-adds personaVersion
  // when this call's value is non-null. The standard fields are re-asserted
  // over the spread so they always win over a same-named stray key, and the
  // explicit `brain` ARG is applied LAST so it always wins over a stale
  // `brain` key that got spread in from a prior deploy's metadata.
  const metadata: Record<string, string> = {
    ...(Object.fromEntries(Object.entries(args.metadata).filter(([, v]) => v !== null)) as Record<string, string>),
    source: args.metadata.source,
    kind: args.metadata.kind,
    persona: args.metadata.persona,
  };
  if (args.metadata.personaVersion !== null) metadata.personaVersion = args.metadata.personaVersion;
  if (args.brain) metadata.brain = args.brain;

  const version = await project.agents.createVersion(args.name, definition, {
    foundryFeatures: "HostedAgents=V1Preview",
    metadata,
  });

  const v = (version as { version?: string | number }).version;
  if (v === undefined) {
    throw new LocalCliError({
      code: "CREATE_VERSION_NO_VERSION",
      message: `createVersion for '${args.name}' returned no version id.`,
    });
  }
  return String(v);
}

/** Read the per-version agent identity OID (instance_identity.principal_id) via REST. */
export async function getAgentIdentityPrincipalId(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  version: string;
}): Promise<string> {
  const url = `${args.endpoint}/agents/${args.name}/versions/${args.version}?api-version=${API}`;
  const data = await authedJson<{ instance_identity?: { principal_id?: string } }>({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "GET",
    url,
  });
  const oid = data?.instance_identity?.principal_id;
  if (!oid) {
    throw new LocalCliError({
      code: "AGENT_IDENTITY_MISSING",
      message: `Could not read the agent identity for '${args.name}' version ${args.version}.`,
      hint: "The hosted-agent version may not have provisioned its identity yet; retry in a few seconds.",
    });
  }
  return oid;
}

/** Poll target: the version's status, lowercased ("creating" | "active" | "failed" | ...). */
export async function getVersionStatus(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  version: string;
}): Promise<string> {
  const url = `${args.endpoint}/agents/${args.name}/versions/${args.version}?api-version=${API}`;
  const data = await authedJson<{ status?: string }>({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "GET",
    url,
  });
  return (data?.status ?? "").toLowerCase();
}

export async function agentExists(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
}): Promise<boolean> {
  const url = `${args.endpoint}/agents/${args.name}?api-version=${API}`;
  const res = await authedJsonResult({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "GET",
    url,
    okStatuses: [404],
  });
  return res.status === 200;
}

export interface PromptMetadata {
  persona: string;
  personaVersion: string | null;
  /** JSON-serialized map of resolved fillable-field values (defaults + overrides), for later recovery by `platform update`. */
  fillableFieldValues?: string;
}

/**
 * Create a PROMPT agent version via the @azure/ai-projects SDK — the prompt
 * sibling of createCoderVersion. No `foundryFeatures` (that's hosted-only).
 * Used by Simple Stacey's fast deploy; reusable by the F6 advisor deploys.
 */
export async function createPromptVersion(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  model: string;
  instructions: string;
  reasoningEffort?: ReasoningEffort;
  metadata: PromptMetadata;
}): Promise<string> {
  const project = new AIProjectClient(args.endpoint, args.credential);

  const definition: PromptAgentDefinition = {
    kind: "prompt",
    model: args.model,
    instructions: args.instructions,
    ...(args.reasoningEffort ? { reasoning: { effort: args.reasoningEffort } } : {}),
  };

  const metadata: Record<string, string> = {
    source: METADATA_SOURCE,
    kind: "prompt",
    persona: args.metadata.persona,
  };
  if (args.metadata.personaVersion !== null) metadata.personaVersion = args.metadata.personaVersion;
  if (args.metadata.fillableFieldValues !== undefined) metadata.fillableFieldValues = args.metadata.fillableFieldValues;

  const version = await project.agents.createVersion(args.name, definition, { metadata });

  const v = (version as { version?: string | number }).version;
  if (v === undefined) {
    throw new LocalCliError({
      code: "CREATE_VERSION_NO_VERSION",
      message: `createVersion for '${args.name}' returned no version id.`,
    });
  }
  return String(v);
}

/**
 * Delete the agent (cascades the container + its identity + role assignment).
 * `force=true` also cascade-deletes active sessions — without it the platform
 * 409s ("Agent has active sessions") for ~15 min after a recent invoke. A
 * teardown is deliberate + confirm-gated, so forcing is the right behavior.
 */
export async function deleteAgent(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
}): Promise<void> {
  const url = `${args.endpoint}/agents/${args.name}?api-version=${API}&force=true`;
  await authedJsonResult({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "DELETE",
    url,
    okStatuses: [404], // already gone — idempotent
  });
}
